import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, merge, of, switchMap, take, tap, zip } from 'rxjs';
import { Router } from '@angular/router';

import { AppointmentApiService, StoreApiService } from '@paymash/sdk/api/v2';
import {
  BookingScheduleAppointmentActions,
  BookingConfirmAppointmentActions,
  BookingScheduleAppointmentApiActions,
  BookingAppointmentActions,
  BookingAppointmentApiActions,
  BookingConfirmAppointmentApiActions,
} from './appointment.actions';

import {
  AuthorizationCustomerActions,
  AuthorizationCustomerApiActions,
  Customer,
  selectCustomerData,
} from '@modules/authorization/store/customer';

import { selectCompanyUuid } from '@public-page/store/company';
import { selectStoreUuid } from '@modules/company-page/store/company-stores';
import {
  selectAppointmentUuid,
  selectDraftAppointmentPayload,
  selectExternalNote,
  selectParamsValue,
} from '@modules/booking/store/appointment/appointment.selectors';
import { selectCheckedServices } from '@modules/booking/store/services';
import { selectQueryParam } from '@public-page/store/router';
import { ActivateAppointmentMapper } from '@modules/core/mappers';
import { NotificationActions } from '@public-page/store/notification';
import { SuccessMessageKeysEnum } from '@modules/core/enums';
import { ErrorMessageKeyService } from '@modules/core/services';
import { GlobalErrorActions } from '@public-page/store/global-error';

@Injectable()
export class AppointmentEffects {
  constructor(
    private actions$: Actions,
    private storeApiService: StoreApiService,
    private appointmentApiService: AppointmentApiService,
    private store: Store,
    private router: Router,
    private activateAppointmentMapper: ActivateAppointmentMapper,
    private errorMessageKeyService: ErrorMessageKeyService
  ) {}

  /* eslint-disable @typescript-eslint/no-unused-vars */

  // todo: refactor to new realization
  schedulePage_setDefaultEmployeeToService$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingScheduleAppointmentActions.componentLoaded),
      switchMap(() =>
        this.store.select(selectCheckedServices).pipe(
          filter((val) => val?.length > 0),
          take(1),
          map((services) => BookingScheduleAppointmentActions.setDefaultEmployee({ checkedServices: services }))
        )
      )
    );
  });

  schedulePage_updateSelectedEmployee$ = createEffect(() => {
    // todo check if will work without setDefaultEmployee action
    return this.actions$.pipe(
      ofType(BookingScheduleAppointmentActions.setDate),
      switchMap(() => {
        return of(BookingScheduleAppointmentActions.updateSelectedEmployees());
      })
    );
  });

  schedulePage_loadDefaultAvailableSlots$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingScheduleAppointmentActions.updateSelectedEmployees),
      switchMap(() => {
        return of(BookingScheduleAppointmentActions.loadAvailableSlots());
      })
    );
  });

  schedulePage_loadAvailableSlots$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingScheduleAppointmentActions.loadAvailableSlots, BookingScheduleAppointmentActions.selectEmployee),
      concatLatestFrom(() => [
        this.store.select(selectCompanyUuid),
        this.store.select(selectStoreUuid),
        this.store.select(selectParamsValue),
      ]),
      exhaustMap(([action, companyUuid, StoreUuid, params]) =>
        this.storeApiService.getSlots(companyUuid, StoreUuid, params).pipe(
          map((data) =>
            BookingScheduleAppointmentApiActions.loadSlotsSuccess({
              services: data.services,
              slots: data.slots,
            })
          ),
          catchError((error) => {
            return merge([
              BookingScheduleAppointmentApiActions.loadSlotsFailure(),
              GlobalErrorActions.handleError({ error }),
            ]);
          })
        )
      )
    );
  });

  schedulePage_clearAvailableSlotTime$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingScheduleAppointmentApiActions.loadSlotsSuccess),
      switchMap(() => {
        return of(BookingScheduleAppointmentActions.clearAvailableSlot());
      })
    );
  });

  schedulePage_createDraftAppointment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingScheduleAppointmentActions.createDraftAppointment),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid), this.store.select(selectDraftAppointmentPayload)]),
      exhaustMap(([action, companyUuid, payload]) =>
        this.appointmentApiService.createDraftAppointment(companyUuid, payload).pipe(
          map((data) => BookingScheduleAppointmentApiActions.createDraftAppointmentSuccess({ appointment: data })),
          catchError((error) =>
            merge([
              BookingScheduleAppointmentApiActions.createDraftAppointmentFailure(),
              GlobalErrorActions.handleError({ error }),
            ])
          )
        )
      )
    );
  });

  // todo: refactor to new realization
  schedulePage_redirectToConfirm$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookingScheduleAppointmentApiActions.createDraftAppointmentSuccess),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              this.router.navigate([companyUuid, 'booking', 'confirm'], {
                queryParams: { storeUuid, appointmentUuid },
                replaceUrl: true, // todo: remove this logic after implementation edit appointment
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // Confirm page

  confirmPage_activateAppointment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingConfirmAppointmentActions.activateAppointment),
      map((action) => this.activateAppointmentMapper.mapToDTO(action.payload)),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid), this.store.select(selectAppointmentUuid)]),
      exhaustMap(([payload, companyUuid, appointmentUuid]) =>
        this.appointmentApiService.activateAppointment(companyUuid, appointmentUuid, payload).pipe(
          map((appointment) => this.activateAppointmentMapper.mapToEntity(appointment)),
          switchMap((entity) => {
            return [
              BookingConfirmAppointmentApiActions.activateAppointmentSuccess({ appointment: entity }),
              NotificationActions.addSuccess({ messageKey: SuccessMessageKeysEnum.CONFIRM_APPOINTMENT_SENT }),
            ];
          }),
          catchError((error) =>
            merge([
              BookingConfirmAppointmentApiActions.activateAppointmentFailure(),
              GlobalErrorActions.handleError({ error }),
            ])
          )
        )
      )
    );
  });

  // todo: refactor to new realization
  confirmPage_RedirectToThankYou$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookingConfirmAppointmentApiActions.activateAppointmentSuccess),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              this.router.navigate([companyUuid, 'booking', 'thank-you'], {
                queryParams: { storeUuid, appointmentUuid },
                replaceUrl: true, // todo: remove this logic after implementation edit appointment
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  confirmPage_deleteAppointment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingConfirmAppointmentActions.deleteDraftAppointment),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid), this.store.select(selectAppointmentUuid)]),
      exhaustMap(([action, companyUuid, appointmentUuid]) =>
        this.appointmentApiService.deleteAppointment(companyUuid, appointmentUuid).pipe(
          map(() => {
            return BookingConfirmAppointmentApiActions.deleteAppointmentSuccess();
          }),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([
              BookingConfirmAppointmentApiActions.deleteAppointmentFailure(),
              NotificationActions.addError({ messageKey }),
            ]);
          })
        )
      )
    );
  });

  loadAppointment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingAppointmentActions.loadAppointment),
      concatLatestFrom(() => [
        this.store.select(selectCompanyUuid),
        this.store.select(selectQueryParam('appointmentUuid')),
      ]),
      exhaustMap(([action, companyUuid, appointmentUuid]) =>
        this.appointmentApiService.getAppointment(companyUuid, appointmentUuid as string).pipe(
          map((appointment) => BookingAppointmentApiActions.loadAppointmentSuccess({ appointment })),
          catchError((error) =>
            merge([BookingAppointmentApiActions.loadAppointmentFailure(), GlobalErrorActions.handleError({ error })])
          )
        )
      )
    );
  });

  callActivateAppointment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationCustomerActions.loadFromRegisterAPI, AuthorizationCustomerApiActions.updateSuccess),
      concatLatestFrom(() => [this.store.select(selectCustomerData), this.store.select(selectExternalNote)]),
      map(([action, customer, comment]) => {
        const payload = this.activateAppointmentMapper.mapToDTO({ ...customer, comment } as Customer);
        return BookingConfirmAppointmentActions.activateAppointment({ payload });
      })
    );
  });
}
